import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import {
  ListingCondition,
  PickupOption,
  getConditionDisplayText,
  getPickupOptionDisplayText
} from '@/types/residents/marketplaceListing';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

const DeliveryIcon = RESIDENTS_SVG_COMPONENTS.DELIVERY;

interface ItemDetailsSectionProps {
  price: string;
  title: string;
  description: string;
  condition: ListingCondition;
  pickupOption: PickupOption;
  isSold?: boolean;
}

/**
 * Reusable item details section showing price, title, description, and pickup option
 */
export const ItemDetailsSection: React.FC<ItemDetailsSectionProps> = ({
  price,
  title,
  description,
  condition,
  pickupOption,
  isSold = false,
}) => {
  const mc = useMarketplaceColors();
  const { semantic } = useThemeColors();

  const styles = useMemo(() => StyleSheet.create({
    container: {
      gap: 6,
    },
    priceRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    priceContainer: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    price: {
      fontSize: 24,
      fontWeight: '700',
      color: mc.textPrimary,
      lineHeight: 36,
    },
    priceDot: {
      fontSize: 24,
      fontWeight: '700',
      color: mc.textPrimary,
      lineHeight: 36,
    },
    soldText: {
      fontSize: 24,
      fontWeight: '500',
      color: semantic.interactive.danger,
      lineHeight: 36,
    },
    condition: {
      fontSize: 12,
      fontWeight: '400',
      color: mc.textSecondary,
      lineHeight: 18,
    },
    title: {
      fontSize: 16,
      fontWeight: '600',
      color: mc.textPrimary,
      lineHeight: 24,
    },
    description: {
      fontSize: 14,
      fontWeight: '400',
      color: mc.textSecondary,
      lineHeight: 21,
    },
    pickupBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      backgroundColor: mc.white5Percent,
      paddingHorizontal: 12,
      height: 28,
      borderRadius: 20,
      alignSelf: 'flex-start',
      marginTop: 10,
    },
    pickupText: {
      fontSize: 12,
      fontWeight: '500',
      color: mc.textSecondary,
      lineHeight: 18,
    },
  }), [mc, semantic]);

  return (
    <View style={styles.container}>
      {/* Price and Condition */}
      <View style={styles.priceRow}>
        <View style={styles.priceContainer}>
          <Text style={styles.price}>{price}</Text>
          {isSold && (
            <>
              <Text style={styles.priceDot}> • </Text>
              <Text style={styles.soldText}>Sold</Text>
            </>
          )}
        </View>
        <Text style={styles.condition}>{getConditionDisplayText(condition)}</Text>
      </View>

      {/* Title */}
      <Text style={styles.title}>{title}</Text>

      {/* Description */}
      <Text style={styles.description}>{description}</Text>

      {/* Pickup Option Badge */}
      <View style={styles.pickupBadge}>
        <DeliveryIcon width={16} height={16} color={mc.textSecondary} />
        <Text style={styles.pickupText}>{getPickupOptionDisplayText(pickupOption)}</Text>
      </View>
    </View>
  );
};
